import { useEffect, useCallback } from 'react';
import authService from '../services/authService';

/**
 * Hook to handle automatic token refresh
 * This hook sets up automatic token refresh and handles token expiration
 */
export const useTokenRefresh = () => {
  const handleTokenRefresh = useCallback(async () => {
    try {
      const success = await authService.refreshTokens();
      if (!success) {
        console.warn('Token refresh failed, redirecting to login');
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      window.location.href = '/login';
    }
  }, []);

  const setupTokenRefresh = useCallback(() => {
    if (!authService.isAuthenticated() || !authService.getAccessToken()) return;

    try {
      const token = authService.getAccessToken();
      if (!token) return;

      // Decode JWT to get expiration time
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      // If token expires in less than 5 minutes, refresh immediately
      if (timeUntilExpiry <= 5 * 60 * 1000) {
        handleTokenRefresh();
        return;
      }

      // Otherwise, refresh token 5 minutes before expiry
      const refreshTime = timeUntilExpiry - 5 * 60 * 1000;

      const timeoutId = setTimeout(() => {
        handleTokenRefresh();
      }, refreshTime);

      // Return cleanup function
      return () => clearTimeout(timeoutId);
    } catch (error) {
      console.error('Error setting up token refresh:', error);
    }
  }, [handleTokenRefresh]);

  useEffect(() => {
    if (authService.isAuthenticated()) {
      const cleanup = setupTokenRefresh();
      return cleanup;
    }
  }, [setupTokenRefresh]);

  // Also set up periodic checks every 5 minutes
  useEffect(() => {
    if (!authService.isAuthenticated()) return;

    const intervalId = setInterval(() => {
      const token = authService.getAccessToken();
      if (!token) return;

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;

        // If token expires in less than 10 minutes, refresh it
        if (timeUntilExpiry <= 10 * 60 * 1000) {
          handleTokenRefresh();
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(intervalId);
  }, [handleTokenRefresh]);

  return {
    refreshTokens: handleTokenRefresh,
  };
};

export default useTokenRefresh;
